import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faRocket, faTrash } from '@fortawesome/free-solid-svg-icons';
import { callQuery } from '../../amplify/appsync/query';
import { callWrite } from '../../amplify/appsync/write';
import { CREATE_RELEASE_NOTE, DELETE_RELEASE_NOTE, UPDATE_RELEASE_NOTE } from '../../amplify/appsync/write';
import { GET_RELEASE_NOTES, ReleaseNote } from '../../amplify/appsync/query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const ReleaseNotesAdmin: React.FC = () => {
    const [notes, setNotes] = useState<ReleaseNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

    // Modal states
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Form data
    const [editingNote, setEditingNote] = useState<ReleaseNote | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        version: '',
        description: '',
    });
    const [noteToDelete, setNoteToDelete] = useState<ReleaseNote | null>(null);

    // Fetch notes on mount
    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        setLoading(true);
        try {
            const result = await callQuery({ operation: GET_RELEASE_NOTES });
            setNotes(result || []);
        } catch (error) {
            console.error('Error fetching release notes:', error);
            setMessage({ type: 'danger', text: 'Failed to load release notes' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateModal = () => {
        setEditingNote(null);
        setFormData({ title: '', version: '', description: '' });
        setShowCreateEditModal(true);
    };

    const handleOpenEditModal = (note: ReleaseNote) => {
        setEditingNote(note);
        setFormData({
            title: note.title,
            version: note.version,
            description: note.description,
        });
        setShowCreateEditModal(true);
    };

    const handleCloseCreateEditModal = () => {
        setShowCreateEditModal(false);
        setEditingNote(null);
        setFormData({ title: '', version: '', description: '' });
    };

    const handleOpenDeleteModal = (note: ReleaseNote) => {
        setNoteToDelete(note);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setNoteToDelete(null);
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.title.trim()) {
            setMessage({ type: 'danger', text: 'Title is required' });
            return;
        }
        if (formData.title.trim().length > 200) {
            setMessage({ type: 'danger', text: 'Title must be 200 characters or less' });
            return;
        }
        if (!formData.version.trim()) {
            setMessage({ type: 'danger', text: 'Version is required' });
            return;
        }
        if (!formData.description.trim()) {
            setMessage({ type: 'danger', text: 'Description is required' });
            return;
        }

        setSaving(true);
        setMessage(null);

        try {
            if (editingNote) {
                // Update existing note
                await callWrite({
                    operation: UPDATE_RELEASE_NOTE,
                    createdAt: editingNote.createdAt,
                    title: formData.title,
                    version: formData.version,
                    description: formData.description,
                });
                setMessage({ type: 'success', text: 'Release note updated successfully' });
            } else {
                // Create new note
                await callWrite({
                    operation: CREATE_RELEASE_NOTE,
                    title: formData.title,
                    version: formData.version,
                    description: formData.description,
                });
                setMessage({ type: 'success', text: 'Release note created successfully' });
            }

            await fetchNotes();
            handleCloseCreateEditModal();
        } catch (error: any) {
            console.error('Error saving release note:', error);
            setMessage({
                type: 'danger',
                text: error.message || 'Failed to save release note. Make sure you are in a production environment.'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!noteToDelete) return;

        setSaving(true);
        setMessage(null);

        try {
            await callWrite({
                operation: DELETE_RELEASE_NOTE,
                createdAt: noteToDelete.createdAt,
            });
            setMessage({ type: 'success', text: 'Release note deleted successfully' });
            await fetchNotes();
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting release note:', error);
            setMessage({
                type: 'danger',
                text: error.message || 'Failed to delete release note'
            });
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStageBadgeVariant = (stage: string) => {
        switch (stage) {
            case 'physiohub':
                return 'primary';
            case 'capacityprod':
                return 'info';
            default:
                return 'secondary';
        }
    };

    return (
        <Container fluid style={{ marginTop: '80px' }}>
            <Row className="justify-content-center">
                <Col lg={10}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2>
                            <FontAwesomeIcon icon={faRocket} /> Release Notes Management
                        </h2>
                        <Button variant="primary" onClick={handleOpenCreateModal}>
                            Create Release Note
                        </Button>
                    </div>

                    {message && (
                        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                            {message.text}
                        </Alert>
                    )}

                    <Alert variant="info">
                        <strong>Note:</strong> Users can view these release notes on their profile page.
                    </Alert>

                    <Card>
                        <Card.Header>
                            <strong>All Release Notes</strong>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center p-4">
                                    <Spinner animation="border" />
                                    <p className="mt-2">Loading release notes...</p>
                                </div>
                            ) : notes.length === 0 ? (
                                <p className="text-muted text-center p-4">
                                    No release notes found. Create your first release note to get started.
                                </p>
                            ) : (
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Version</th>
                                            <th>Title</th>
                                            <th>Created</th>
                                            <th>Created By</th>
                                            <th>Stage</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notes && Array.isArray(notes) && notes.map((note) => (
                                            <tr key={note.createdAt}>
                                                <td>
                                                    <Badge bg="secondary">{note.version}</Badge>
                                                </td>
                                                <td>{note.title}</td>
                                                <td>{formatDate(note.createdAt)}</td>
                                                <td>{note.createdBy}</td>
                                                <td>
                                                    <Badge bg={getStageBadgeVariant(note.stage)}>
                                                        {note.stage}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={() => handleOpenEditModal(note)}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} /> Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleOpenDeleteModal(note)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} /> Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Create/Edit Modal */}
                    <Modal show={showCreateEditModal} onHide={handleCloseCreateEditModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {editingNote ? 'Edit Release Note' : 'Create Release Note'}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Version *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., 2.5.1"
                                        value={formData.version}
                                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                    />
                                    <Form.Text className="text-muted">
                                        Semantic version number (e.g., 2.5.1)
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Title * (max 200 characters)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Brief summary of the release"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        maxLength={200}
                                    />
                                    <Form.Text className="text-muted">
                                        {formData.title.length} / 200 characters
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description * (Markdown supported)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        placeholder="Detailed description of changes, improvements, and fixes..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                    <Form.Text className="text-muted">
                                        You can use Markdown formatting (headers, lists, bold, italic, links, etc.)
                                    </Form.Text>
                                </Form.Group>

                                {formData.description && (
                                    <Card className="mb-3">
                                        <Card.Header>
                                            <strong>Preview</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {formData.description}
                                            </ReactMarkdown>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseCreateEditModal} disabled={saving}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    <>{editingNote ? 'Update' : 'Create'}</>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Delete Confirmation Modal */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete Release Note</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Alert variant="warning">
                                <strong>Warning:</strong> This action cannot be undone.
                            </Alert>
                            {noteToDelete && (
                                <p>
                                    Are you sure you want to delete the release note for version{' '}
                                    <strong>{noteToDelete.version}</strong>?
                                </p>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={handleDelete} disabled={saving}>
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Col>
            </Row>
        </Container>
    );
};
